async function getProvinceOptions() {
  const { locale } = useI18n();

  const provinceData = await useFetch("/api/data", {
    method: "POST",
    body: { action: "province", locale: locale.value },
  });
  const provinceOptionsObj = provinceData.data.value?.data?.provinces;
  return Object.keys(provinceOptionsObj).map((key) => ({
    value: key,
    province: provinceOptionsObj[key],
  }));
}

async function getTermsOptions() {
  const { locale } = useI18n();

  const orderedTerms = {} as any;
  const orderedKeys = [
    "gdpr_privacy",
    "gdpr_newsletter",
    "gdpr_profiling",
    "gdpr_marketing_analysis",
    "gdpr_contest_opt_in",
    "gdpr_external_consent",
  ];

  const termsData = await useFetch("/api/data", {
    method: "POST",
    body: { action: "terms", locale: locale.value },
  });

  const terms = termsData.data.value?.data?.terms ?? [];

  orderedKeys.forEach((key) => {
    if (key in terms) {
      orderedTerms[key] = terms[key];
    }
  });

  return orderedTerms;
}

async function getStoresOptions() {
  const { locale } = useI18n();

  const storesData = await useFetch("/api/data", {
    method: "POST",
    body: { action: "stores", locale: locale.value },
  });

  return storesData.data.value?.data?.stores ?? [];
}

async function getMobilePrefixesOptions() {
  const { locale } = useI18n();

  const mobileData = await useFetch("/api/data", {
    method: "POST",
    body: { action: "mobile", locale: locale.value },
  });

  const mobilePrefixesOptionsObj = mobileData.data.value?.data?.calling_codes;
  return Object.keys(mobilePrefixesOptionsObj).map((key) => ({
    country: key + " " + "(+" + mobilePrefixesOptionsObj[key] + ")",
    value: mobilePrefixesOptionsObj[key],
  }));
}

// Splits a stored "<prefix>-<number>" phone at the first dash, like the backend does.
// Phones synced without a prefix have no dash, so the whole value is the number.
function splitCellPhone(cellPhone: string | null | undefined, defaultPrefix: string) {
  const value = cellPhone ?? "";
  const dashIndex = value.indexOf("-");

  if (dashIndex === -1) {
    return { prefix: defaultPrefix, number: value };
  }

  return {
    prefix: value.slice(0, dashIndex) || defaultPrefix,
    number: value.slice(dashIndex + 1),
  };
}

function removeEmptyStringKeys(
  obj: Record<string, any>,
  exeptKeys: string[] = [
    "customer_gender",
    "customer_childrens",
    "customer_province",
    "customer_zip",
    "customer_city",
    "customer_address",
  ]
): Record<string, any> {
  const clean = (value: any, key?: string): any => {
    if (key && exeptKeys.includes(key)) {
      // If the key is exept, return the value as is
      return value;
    }

    if (typeof value === "string") {
      return value.trim() === "" ? undefined : value;
    }

    if (Array.isArray(value)) {
      const cleanedArray = value
        .map((item) => clean(item))
        .filter((item) => item !== undefined);
      return cleanedArray.length === 0 ? undefined : cleanedArray;
    }

    if (typeof value === "object" && value !== null) {
      const cleanedObject: Record<string, any> = {};
      for (const [key, val] of Object.entries(value)) {
        const cleanedVal = clean(val, key);
        if (cleanedVal !== undefined) {
          cleanedObject[key] = cleanedVal;
        }
      }
      return Object.keys(cleanedObject).length === 0
        ? undefined
        : cleanedObject;
    }

    return value;
  };

  // Clean the object and remove keys with undefined values
  const result = clean(obj);
  return result === undefined ? {} : result;
}

export {
  getMobilePrefixesOptions,
  getProvinceOptions,
  getTermsOptions,
  getStoresOptions,
  removeEmptyStringKeys,
  splitCellPhone,
};
